import { useEffect, memo } from "react";
import PropTypes from 'prop-types';
import { Users, AlertTriangle } from "lucide-react";
import IMAGES from "../../../../constant/images";
import { useDispatch, useSelector } from "react-redux";
import { getClientMemberStatsApi } from "../../../../redux/action/Client-Member";
import useIsMobile from "../shared/Mobile-Responsive";

// Icon mapping for different stat types
const STAT_ICONS = {
  total: IMAGES.completed_member_icon,
  highModerate: IMAGES.practitioner_icon
};

// Default stats structure for mapping API data to display
const getStatsFromApiData = (statsData) => {
  if (!statsData) return [];

  const statsConfig = [
    { id: 'total', dataKey: 'totalMembers', title: 'Total Members' },
    { id: 'highModerate', dataKey: 'highRiskCases', title: 'High and Moderate Risk Cases' }
  ];

  return statsConfig.map(config => ({
    id: config.id,
    icon: STAT_ICONS[config.id],
    value: statsData[config.dataKey] || 0,
    title: config.title
  }));
};

// Presentational lucide icon + tone per stat id (falls back to the image icon)
const STAT_LUCIDE = {
  total: { Icon: Users, tone: 'bg-[#EFF6FF] text-[#2563EB]' },
  highModerate: { Icon: AlertTriangle, tone: 'bg-[#FFF7ED] text-[#EA580C]' }
};

// Reusable class constants (stacked layout, same as the practitioner Quick Stats cards)
const CLASSES = {
  CARD: 'flex-shrink-0 flex flex-col min-w-[220px] w-full rounded-2xl border-[1px] border-solid p-3 bg-white shadow-sm cursor-pointer hover:shadow-md transition-shadow',
  CARD_DEFAULT: 'border-[#E2E8F0]',
  CARD_SELECTED: 'border-[#2563EB] ring-2 ring-[#BFDBFE]',
  ICON_CONTAINER: 'flex items-center justify-center w-[38px] h-[38px] rounded-xl flex-shrink-0 bg-[#EFF6FF] text-[#2563EB]',
  ICON: 'w-[22px] h-[22px]',
  VALUE: 'text-[26px] leading-tight font-bold text-[#171717] font-poppins',
  TITLE: 'mt-0.5 text-[13px] uppercase tracking-wide font-medium text-[#64748B] font-poppins'
};

const StatCard = memo(({ icon, value, title, id, onClick, isSelected }) => {
  const lucide = STAT_LUCIDE[id];
  return (
  <div
    className={`${CLASSES.CARD} ${isSelected ? CLASSES.CARD_SELECTED : CLASSES.CARD_DEFAULT}`}
    onClick={() => onClick && onClick(id)}
    role="button"
    tabIndex={0}
    aria-pressed={isSelected}
    aria-label={`Filter by ${title.toLowerCase()}`}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick && onClick(id);
      }
    }}
  >
    {/* Icon tile */}
    <div className="flex items-start justify-between mb-1.5">
      <div className={lucide ? `${CLASSES.ICON_CONTAINER} ${lucide.tone}` : CLASSES.ICON_CONTAINER}>
        {lucide ? (
          <lucide.Icon size={18} />
        ) : (
          <img
            src={icon}
            alt={`${title} icon`}
            className={CLASSES.ICON}
            loading="lazy"
          />
        )}
      </div>
    </div>

    {/* Text Section */}
    <div className={CLASSES.VALUE} aria-label={`${value} ${title.toLowerCase()}`}>
      {value.toLocaleString()}
    </div>
    <div className={CLASSES.TITLE}>{title}</div>
  </div>
  );
});

StatCard.displayName = 'StatCard';

StatCard.propTypes = {
  icon: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  isSelected: PropTypes.bool
};

function MemberCounter({ stats, onStatClick, selectedStatId }) {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const { clientMemberStats, loading, error } = useSelector((state) => state.CLIENT_MEMBER);

  useEffect(() => {
    dispatch(getClientMemberStatsApi());
  }, [dispatch]);

  // Use provided stats prop, or derive from Redux data, or show loading/error states
  const displayStats = stats || getStatsFromApiData(clientMemberStats);

  if (loading) {
    const LoadingSkeleton = () => (
      <div className="flex-shrink-0 flex flex-col min-w-[220px] w-full rounded-2xl border-[1px] border-solid border-[#E2E8F0] p-3 bg-white shadow-sm animate-pulse">
        <div className="w-[38px] h-[38px] bg-[#F1F5F9] rounded-xl mb-1.5"></div>
        <div className="h-7 bg-gray-200 rounded w-16 mb-1"></div>
        <div className="h-4 bg-gray-200 rounded w-32"></div>
      </div>
    );

    return (
      <div className="p-0 mb-4">
        <div
          className={
            isMobile
              ? "flex gap-2.5 overflow-x-auto flex-nowrap"
              : "grid grid-cols-1 md:grid-cols-3 gap-2.5"
          }
        >
          {Array.from({ length: 2 }, (_, i) => (
            <LoadingSkeleton key={`skeleton-${i}`} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-0 mb-4">
        <div className="text-[#DC2626] text-sm font-poppins text-center p-4">
          Error loading member statistics: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-0 mb-4">
      <div
        className={
          isMobile
            ? "flex gap-2.5 overflow-x-auto flex-nowrap"
            : "grid grid-cols-1 md:grid-cols-3 gap-2.5"
        }
      >
        {displayStats.map((stat) => (
          <StatCard
            key={stat.id}
            id={stat.id}
            icon={stat.icon}
            value={stat.value}
            title={stat.title}
            onClick={onStatClick}
            isSelected={selectedStatId === stat.id}
          />
        ))}
      </div>
    </div>
  );
}

MemberCounter.propTypes = {
  stats: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired
    })
  ),
  onStatClick: PropTypes.func,
  selectedStatId: PropTypes.string
};

export default MemberCounter;
